import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../../contexts/authContext';
import { uploadAndPublishSnap, uploadAndPublishImageMusicSnap, getActiveCount, getTracks } from '../../api/linguaSnaps';
import './LinguaSnaps.css';
import { FaUpload, FaVideo, FaImage, FaMusic, FaCheck, FaSpinner, FaTimes, FaPlay, FaPause, FaVolumeUp, FaVolumeMute } from 'react-icons/fa';

const LANGUAGES = [
  { code: 'en', name: 'English' },
  { code: 'es', name: 'Spanish' },
  { code: 'fr', name: 'French' },
  { code: 'de', name: 'German' },
  { code: 'it', name: 'Italian' },
  { code: 'pt', name: 'Portuguese' },
  { code: 'zh', name: 'Chinese' },
  { code: 'ja', name: 'Japanese' },
  { code: 'ko', name: 'Korean' },
  { code: 'ar', name: 'Arabic' },
  { code: 'ru', name: 'Russian' },
  { code: 'hi', name: 'Hindi' },
  { code: 'nl', name: 'Dutch' },
  { code: 'pl', name: 'Polish' },
  { code: 'tr', name: 'Turkish' },
];

const CEFR_LEVELS = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2'];
const DURATION_OPTIONS = [
  { value: 30, label: '30 seconds' },
  { value: 60, label: '60 seconds' },
];

const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100MB

const LinguaSnapUpload = ({ onClose, onComplete }) => {
  const { user } = useAuth();
  // Mode: 'video' or 'image_music'
  const [mode, setMode] = useState('video');
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [caption, setCaption] = useState('');
  const [targetLanguage, setTargetLanguage] = useState('');
  const [cefrLevel, setCefrLevel] = useState('');
  const [maxDuration, setMaxDuration] = useState(30);
  const [musicTrackId, setMusicTrackId] = useState('');
  const [tracks, setTracks] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [status, setStatus] = useState('');
  const [error, setError] = useState(null);
  const [activeCount, setActiveCount] = useState(null);
  const [success, setSuccess] = useState(false);
  const fileInputRef = useRef(null);
  const videoPreviewRef = useRef(null);
  const audioPreviewRef = useRef(null);
  const [audioReady, setAudioReady] = useState(false);
  const [audioDuration, setAudioDuration] = useState(0);
  const [audioTime, setAudioTime] = useState(0);
  const [audioPlaying, setAudioPlaying] = useState(false);
  const [audioMuted, setAudioMuted] = useState(false);
  const [audioVolume, setAudioVolume] = useState(0.8);
  const [autoplayBlocked, setAutoplayBlocked] = useState(false);

  // Check active count and fetch tracks on mount
  useEffect(() => {
    getActiveCount()
      .then(({ count, max }) => setActiveCount({ count, max }))
      .catch(() => {});
    getTracks()
      .then(({ tracks }) => {
        setTracks(tracks || []);
        if (tracks?.length > 0) setMusicTrackId(tracks[0].id);
      })
      .catch(() => {});
  }, []);

  const formatTime = (sec) => {
    if (!Number.isFinite(sec) || sec <= 0) return '0:00';
    const m = Math.floor(sec / 60);
    const s = Math.floor(sec % 60);
    return `${m}:${String(s).padStart(2, '0')}`;
  };

  const getTrackSrc = (trackId) => {
    if (!trackId) return '';
    return `/api/lingua-snaps/tracks/${encodeURIComponent(trackId)}`;
  };

  const syncAudioStateFromEl = (audioEl) => {
    setAudioTime(audioEl.currentTime || 0);
    setAudioDuration(audioEl.duration || 0);
    setAudioPlaying(!audioEl.paused);
    setAudioMuted(audioEl.muted);
    setAudioVolume(Number.isFinite(audioEl.volume) ? audioEl.volume : 0.8);
  };

  useEffect(() => {
    const audioEl = audioPreviewRef.current;
    if (!audioEl) return;

    const handleLoaded = () => {
      setAudioReady(true);
      syncAudioStateFromEl(audioEl);
    };
    const handleTime = () => syncAudioStateFromEl(audioEl);
    const handlePlay = () => setAudioPlaying(true);
    const handlePause = () => setAudioPlaying(false);
    const handleEnded = () => setAudioPlaying(false);

    audioEl.addEventListener('loadedmetadata', handleLoaded);
    audioEl.addEventListener('timeupdate', handleTime);
    audioEl.addEventListener('play', handlePlay);
    audioEl.addEventListener('pause', handlePause);
    audioEl.addEventListener('ended', handleEnded);

    return () => {
      audioEl.removeEventListener('loadedmetadata', handleLoaded);
      audioEl.removeEventListener('timeupdate', handleTime);
      audioEl.removeEventListener('play', handlePlay);
      audioEl.removeEventListener('pause', handlePause);
      audioEl.removeEventListener('ended', handleEnded);
    };
  }, []);

  useEffect(() => {
    const audioEl = audioPreviewRef.current;
    if (!audioEl) return;

    audioEl.volume = audioVolume;
  }, [audioVolume]);

  useEffect(() => {
    const audioEl = audioPreviewRef.current;
    if (!audioEl) return;

    audioEl.muted = audioMuted;
  }, [audioMuted]);

  useEffect(() => {
    const audioEl = audioPreviewRef.current;
    if (!audioEl) return;

    if (mode !== 'image_music') {
      audioEl.pause();
      setAudioPlaying(false);
      return;
    }

    if (!musicTrackId) {
      audioEl.pause();
      setAudioPlaying(false);
      setAudioReady(false);
      setAudioTime(0);
      setAudioDuration(0);
      return;
    }

    setAutoplayBlocked(false);
    setAudioReady(false);
    setAudioTime(0);
    setAudioDuration(0);

    audioEl.src = getTrackSrc(musicTrackId);
    audioEl.load();

    const tryPlay = async () => {
      try {
        await audioEl.play();
      } catch (e) {
        setAutoplayBlocked(true);
        setAudioPlaying(false);
      }
    };

    void tryPlay();
  }, [mode, musicTrackId]);

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    // Validate file type based on mode
    if (mode === 'video') {
      if (!selectedFile.type.startsWith('video/')) {
        setError('Please select a video file');
        return;
      }
    } else {
      if (!selectedFile.type.startsWith('image/')) {
        setError('Please select an image file');
        return;
      }
    }

    // Validate file size
    if (selectedFile.size > MAX_FILE_SIZE) {
      setError(`File too large. Maximum size is ${MAX_FILE_SIZE / (1024 * 1024)}MB`);
      return;
    }

    setError(null);
    setFile(selectedFile);
    setPreview(URL.createObjectURL(selectedFile));
  };

  const handleVideoDurationCheck = () => {
    // For video mode, warn if video exceeds selected max duration (will be auto-trimmed server-side)
    if (videoPreviewRef.current && mode === 'video') {
      const duration = videoPreviewRef.current.duration;
      if (duration > maxDuration) {
        setError(`Note: Video is ${Math.round(duration)}s. It will be automatically trimmed to ${maxDuration}s.`);
      }
    }
  };

  const handleModeChange = (newMode) => {
    if (newMode !== mode) {
      setMode(newMode);
      handleClearFile();
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const droppedFile = e.dataTransfer?.files?.[0];
    if (droppedFile) {
      const fakeEvent = { target: { files: [droppedFile] } };
      handleFileSelect(fakeEvent);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file) {
      setError(mode === 'video' ? 'Please select a video' : 'Please select an image');
      return;
    }

    if (mode === 'image_music' && !musicTrackId) {
      setError('Please select a background music track');
      return;
    }

    if (activeCount && activeCount.count >= activeCount.max) {
      setError(`You've reached the maximum of ${activeCount.max} active LinguaSnaps. Wait for some to expire or delete them.`);
      return;
    }

    try {
      setUploading(true);
      setError(null);
      setProgress(0);

      if (mode === 'video') {
        await uploadAndPublishSnap({
          file,
          caption: caption.trim() || null,
          targetLanguage: targetLanguage || null,
          cefrLevel: cefrLevel || null,
          maxDurationSeconds: maxDuration,
          onProgress: setProgress,
          onStatus: setStatus,
        });
      } else {
        await uploadAndPublishImageMusicSnap({
          imageFile: file,
          musicTrackId,
          caption: caption.trim() || null,
          targetLanguage: targetLanguage || null,
          cefrLevel: cefrLevel || null,
          maxDurationSeconds: maxDuration,
          onProgress: setProgress,
          onStatus: setStatus,
        });
      }

      setSuccess(true);
      setTimeout(() => {
        onComplete?.();
      }, 1500);
    } catch (err) {
      console.error('[LinguaSnapUpload] Error:', err);
      setError(err.message || 'Failed to upload LinguaSnap');
    } finally {
      setUploading(false);
    }
  };

  const handleClearFile = () => {
    setFile(null);
    setPreview(null);
    setError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const canSubmit = file && !uploading && !success && (mode === 'video' || musicTrackId);

  return (
    <div className="lingua-snap-upload-overlay" onClick={onClose}>
      <div className="lingua-snap-upload" onClick={(e) => e.stopPropagation()}>
        <div className="lingua-snap-upload-header">
          <h2>Create LinguaSnap</h2>
          <button className="lingua-snap-close" onClick={onClose}>
            <span aria-hidden="true">×</span>
          </button>
        </div>

        {activeCount && (
          <div className="lingua-snap-upload-quota">
            <span>
              {activeCount.count} / {activeCount.max} active LinguaSnaps
            </span>
            {activeCount.count >= activeCount.max && (
              <span className="quota-warning">Maximum reached!</span>
            )}
          </div>
        )}

        {success ? (
          <div className="lingua-snap-upload-success">
            <FaCheck className="success-icon" />
            <h3>LinguaSnap Published!</h3>
            <p>Your snap is now live for 12 hours.</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            {/* Mode selector */}
            <div className="lingua-snap-mode-selector">
              <button
                type="button"
                className={`mode-btn ${mode === 'video' ? 'active' : ''}`}
                onClick={() => handleModeChange('video')}
              >
                <FaVideo /> Video
              </button>
              <button
                type="button"
                className={`mode-btn ${mode === 'image_music' ? 'active' : ''}`}
                onClick={() => handleModeChange('image_music')}
              >
                <FaImage /> Image + Music
              </button>
            </div>

            {/* File upload area */}
            <div
              className={`lingua-snap-dropzone ${file ? 'has-file' : ''}`}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onClick={() => !file && fileInputRef.current?.click()}
            >
              {preview ? (
                <div className="lingua-snap-preview-container">
                  {mode === 'video' ? (
                    <video
                      ref={videoPreviewRef}
                      src={preview}
                      className="lingua-snap-preview-video"
                      muted
                      playsInline
                      onLoadedMetadata={handleVideoDurationCheck}
                      controls
                    />
                  ) : (
                    <img
                      src={preview}
                      className="lingua-snap-preview-image"
                      alt="Preview"
                    />
                  )}
                  <button
                    type="button"
                    className="lingua-snap-clear-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleClearFile();
                    }}
                  >
                    <FaTimes />
                  </button>
                </div>
              ) : (
                <div className="lingua-snap-dropzone-content">
                  {mode === 'video' ? <FaVideo className="dropzone-icon" /> : <FaImage className="dropzone-icon" />}
                  <p>Drag & drop {mode === 'video' ? 'a video' : 'an image'} or click to browse</p>
                  <span className="dropzone-hint">
                    Max {MAX_FILE_SIZE / (1024 * 1024)}MB
                  </span>
                </div>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept={mode === 'video' ? 'video/*' : 'image/*'}
                onChange={handleFileSelect}
                style={{ display: 'none' }}
              />
            </div>

            {/* Music track selector (image_music mode only) */}
            {mode === 'image_music' && (
              <div className="lingua-snap-field">
                <label><FaMusic /> Background Music</label>
                <select
                  value={musicTrackId}
                  onChange={(e) => setMusicTrackId(e.target.value)}
                >
                  {tracks.length === 0 ? (
                    <option value="">No tracks available</option>
                  ) : (
                    tracks.map((track) => (
                      <option key={track.id} value={track.id}>
                        {track.name}
                      </option>
                    ))
                  )}
                </select>

                <div className="lingua-snap-audio-player" aria-label="Background music preview">
                  <audio ref={audioPreviewRef} preload="metadata" />

                  <button
                    type="button"
                    className="snap-audio-btn snap-audio-play"
                    onClick={async () => {
                      const audioEl = audioPreviewRef.current;
                      if (!audioEl) return;

                      setAutoplayBlocked(false);
                      if (audioEl.paused) {
                        try {
                          await audioEl.play();
                        } catch (e) {
                          setAutoplayBlocked(true);
                          setAudioPlaying(false);
                        }
                      } else {
                        audioEl.pause();
                      }
                    }}
                    aria-label={audioPlaying ? 'Pause preview' : 'Play preview'}
                    title={audioPlaying ? 'Pause' : 'Play'}
                    disabled={!musicTrackId}
                  >
                    {audioPlaying ? <FaPause /> : <FaPlay />}
                  </button>

                  <div className="snap-audio-times">
                    <span className="snap-audio-time">{formatTime(audioTime)}</span>
                    <span className="snap-audio-time-sep">/</span>
                    <span className="snap-audio-time">{formatTime(audioDuration)}</span>
                  </div>

                  <input
                    className="snap-audio-seek"
                    type="range"
                    min={0}
                    max={audioDuration || 0}
                    step={0.01}
                    value={Math.min(audioTime, audioDuration || 0)}
                    onChange={(e) => {
                      const audioEl = audioPreviewRef.current;
                      if (!audioEl) return;
                      const next = Number(e.target.value);
                      audioEl.currentTime = next;
                      setAudioTime(next);
                    }}
                    disabled={!audioReady}
                    aria-label="Seek"
                  />

                  <button
                    type="button"
                    className="snap-audio-btn snap-audio-mute"
                    onClick={() => setAudioMuted((v) => !v)}
                    aria-label={audioMuted ? 'Unmute preview' : 'Mute preview'}
                    title={audioMuted ? 'Unmute' : 'Mute'}
                    disabled={!musicTrackId}
                  >
                    {audioMuted || audioVolume === 0 ? <FaVolumeMute /> : <FaVolumeUp />}
                  </button>

                  <input
                    className="snap-audio-volume"
                    type="range"
                    min={0}
                    max={1}
                    step={0.01}
                    value={audioMuted ? 0 : audioVolume}
                    onChange={(e) => {
                      const v = Number(e.target.value);
                      setAudioMuted(v === 0);
                      setAudioVolume(v);
                    }}
                    disabled={!musicTrackId}
                    aria-label="Volume"
                  />
                </div>

                {autoplayBlocked && (
                  <div className="lingua-snap-audio-hint">Tap play to preview (autoplay was blocked by your browser)</div>
                )}
              </div>
            )}

            {/* Duration selector */}
            <div className="lingua-snap-field">
              <label>Duration</label>
              <div className="lingua-snap-duration-selector">
                {DURATION_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    className={`duration-btn ${maxDuration === opt.value ? 'active' : ''}`}
                    onClick={() => setMaxDuration(opt.value)}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              {mode === 'video' && (
                <span className="duration-hint">Videos longer than {maxDuration}s will be auto-trimmed</span>
              )}
            </div>

            {/* Caption */}
            <div className="lingua-snap-field">
              <label>Caption (optional)</label>
              <textarea
                value={caption}
                onChange={(e) => setCaption(e.target.value)}
                placeholder="Add a caption for your LinguaSnap..."
                maxLength={200}
                rows={2}
              />
              <span className="char-count">{caption.length}/200</span>
            </div>

            {/* Language & Level */}
            <div className="lingua-snap-field-row">
              <div className="lingua-snap-field">
                <label>Target Language (optional)</label>
                <select
                  value={targetLanguage}
                  onChange={(e) => setTargetLanguage(e.target.value)}
                >
                  <option value="">Select language...</option>
                  {LANGUAGES.map((lang) => (
                    <option key={lang.code} value={lang.code}>
                      {lang.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="lingua-snap-field">
                <label>CEFR Level (optional)</label>
                <select
                  value={cefrLevel}
                  onChange={(e) => setCefrLevel(e.target.value)}
                >
                  <option value="">Select level...</option>
                  {CEFR_LEVELS.map((level) => (
                    <option key={level} value={level}>
                      {level}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Error */}
            {error && <div className="lingua-snap-error">{error}</div>}

            {/* Progress */}
            {uploading && (
              <div className="lingua-snap-progress">
                <div className="lingua-snap-progress-bar-upload">
                  <div
                    className="lingua-snap-progress-fill-upload"
                    style={{ width: `${progress}%` }}
                  />
                </div>
                <span className="lingua-snap-status">{status}</span>
              </div>
            )}

            {/* Submit button */}
            <button
              type="submit"
              className="lingua-snap-submit-btn"
              disabled={!canSubmit}
            >
              {uploading ? (
                <>
                  <FaSpinner className="spinning" /> Uploading...
                </>
              ) : (
                <>
                  <FaUpload /> Publish LinguaSnap
                </>
              )}
            </button>

            <p className="lingua-snap-disclaimer">
              LinguaSnaps are visible to your connections for 12 hours.
            </p>
          </form>
        )}
      </div>
    </div>
  );
};

export default LinguaSnapUpload;
